import { FaSync, FaPaperPlane, FaExclamationTriangle, FaArrowLeft } from 'react-icons/fa'
import { useParams, useNavigate } from 'react-router-dom'
import { useMessages, useSendMessage, useMarkMessagesRead } from '@/hooks/useChat'
import { useEffect, useState, useRef } from 'react'
import Button from '@/components/common/Button/Button'
import Input from '@/components/forms/Input/Input'
import useAuthStore from '@/stores/AuthStore'
import './ChatDetail.css'

const ChatDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const conversationId = id ? Number(id) : 0
    const { data, isLoading, isError, refetch } = useMessages(conversationId)
    const { mutate: sendMessage, isPending: isSending } = useSendMessage()
    const { mutate: markMessagesRead } = useMarkMessagesRead()
    const [text, setText] = useState('')
    const { getUserDetails } = useAuthStore()
    const [userType, setUserType] = useState<'provider' | 'receiver'>('receiver')
    const messagesEndRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!conversationId || isNaN(conversationId)) return

        markMessagesRead(conversationId)
        getUserDetails().then(u => setUserType(u?.user_type || 'receiver'))
    }, [conversationId, markMessagesRead, getUserDetails])

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (data && data.length > 0) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
    }, [data])

    const handleSend = () => {
        if (!text.trim() || !conversationId || isNaN(conversationId)) return
        sendMessage({ conversation: conversationId, content: text.trim() }, {
            onSuccess: () => {
                setText('')
                refetch()
            },
            onError: (error: any) => {
                console.error('Error sending message:', error)
                // Error will be handled by React Query's error state
            }
        })
    }

    // Handle invalid conversation ID
    if (!conversationId || isNaN(conversationId)) {
        return (
            <div className="chat-detail-page">
                <div className="chat-detail-container">
                    <div className="chat-detail-loading">
                        <p>ID de conversación inválido</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="chat-detail-page">
            {/* Header */}
            <div className="chat-detail-header">
                <Button variant="ghost" onClick={() => navigate(-1)}>
                    <FaArrowLeft /> Atrás
                </Button>
                <div className="chat-detail-header-content">
                    {/* <h1 className="chat-detail-title">Conversación</h1> */}
                    <div className="chat-detail-status">
                        <span className="chat-detail-status-dot"></span>
                        En línea
                    </div>
                </div>
                <Button variant="secondary" size="md" onClick={() => refetch()}>
                    <FaSync /> Actualizar
                </Button>
            </div>

            <div className="chat-detail-container">

                {/* Warning Banner */}
                <div className="chat-detail-warning">
                    <FaExclamationTriangle className="chat-detail-warning-icon" />
                    <span>No compartas información personal (teléfono, dirección, email)</span>
                </div>

                {/* Messages */}
                {isLoading ? (
                    <div className="chat-detail-loading">
                        <p>Cargando mensajes...</p>
                    </div>
                ) : isError ? (
                    <div className="chat-detail-loading">
                        <p>Error al cargar mensajes</p>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                            {isError ? 'No se pudo acceder a la conversación. Verifica que tengas permisos.' : ''}
                        </p>
                        <Button variant="secondary" size="md" onClick={() => refetch()} style={{ marginTop: '1rem' }}>
                            Reintentar
                        </Button>
                    </div>
                ) : (
                    <div className="chat-detail-messages">
                    {(data || []).map((m: any) => {
                        const isMe = m.sender_type === userType
                        const senderName = isMe ? 'Tú' : (m.sender_type === 'provider' ? 'Proveedor' : 'Cliente')
                        // Parse UTC date and convert to user's local timezone
                        const messageDate = new Date(m.created)
                        const time = messageDate.toLocaleTimeString('es-PE', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                        })
                        
                        return (
                            <div key={m.id} className={`chat-bubble ${ isMe ? 'chat-bubble--me' : 'chat-bubble--them'}`}>
                                <div className="chat-bubble-container">
                                    {!isMe && <div className="chat-bubble-sender">{senderName}</div>}
                                    <div className="chat-bubble-text">{m.message || m.content}</div>
                                    <div className="chat-bubble-time">{time}</div>
                                </div>
                            </div>
                        )
                    })}
                    {(!data || data.length === 0) && (
                        <div className="chat-detail-empty">
                            <p>Sin mensajes</p>
                            <span>Inicia la conversación enviando un mensaje</span>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                    </div>
                )}

                {/* Composer */}
                <div className="chat-detail-composer">
                    <div className="chat-detail-input-wrapper">
                        <Input
                            name="message"
                            type="text"
                            value={text}
                            setValue={setText}
                            placeholder="Escribe un mensaje..."
                            isDisabled={isSending}
                            fullWidth
                        />
                    </div>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSend}
                        disabled={isSending || !text.trim() || !conversationId || isNaN(conversationId)}
                    >
                        <FaPaperPlane />
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default ChatDetail


